import { CalendarDaysIcon } from "@heroicons/react/24/solid";
import { groupBy } from "lodash";
import Link from "next/link";
import { useEffect, useState } from "react";

import MainContainer from "@/client/components/common/MainContainer";
import MetadataHead from "@/client/components/common/MetdataHead";
import Button from "@/client/components/frame-design-system/buttons/Button";
import { SectionTitle } from "@/client/components/frame-design-system/text/SectionTitle";
import { Close } from "@/client/components/icons/close";
import { Spinner } from "@/client/components/icons/spinner";
import { getCalendarEvents } from "@/client/lib/api";
import {
  formatGoogleCalendarLink,
  formatGoogleCalendarLinkForCollection,
} from "@/client/lib/helpers";
import { CalendarEvent, ReleaseEvent } from "@/shared/types/calendar";
import { MintState } from "@/shared/types/collection";
import { External } from "../../icons/external";
import { BrandText } from "../../frame-design-system/text/BrandText";

const META_TITLE = "Art Blocks - Calendar";

const CalendarPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pastEvents, setPastEvents] = useState<CalendarEvent[]>([]);
  const [futureEvents, setFutureEvents] = useState<CalendarEvent[]>([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const events = await getCalendarEvents();

      const { past, future } = groupByFuturePast(events);
      setPastEvents(past || []);
      setFutureEvents(future || []);
    } catch (err) {
      setError("Failed to fetch events. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MetadataHead title={META_TITLE} />
      <MainContainer>
        <section className="mt-20 mb-12 dark:text-white">
          <div className="flex-col flex sm:flex-row items-center justify-between mb-12">
            <SectionTitle title="Calendar" />
          </div>
        </section>

        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} />
        ) : (
          <div>
            <div>
              <p className="font-medium mb-4 dark:text-white">
                Upcoming Events
              </p>
              <CalendarEventList events={futureEvents} sort="asc" />
            </div>
            <div className="mt-8 mb-20">
              <p className="font-medium mb-4 dark:text-white">Just Released</p>
              <CalendarEventList events={pastEvents} />
            </div>
          </div>
        )}
      </MainContainer>
    </>
  );
};

const groupByDate = (events: CalendarEvent[]) => {
  return groupBy(events, (event) => {
    const date = new Date(event.date);
    const key = formatDateGroupKey(date, event.timezone);
    return key;
  });
};

/**
 * Format a date to be grouped in the format 2020-12-31
 *
 * If you pass a timezone it will use the date of that timezone otherwise it will use the browsers timezones
 */
const formatDateGroupKey = (date: Date, timezone?: string) => {
  const dateTimezoneString = date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: timezone || undefined,
  });
  const [month, day, year] = dateTimezoneString.split("/");
  const dateString = `${year}-${month}-${day}`;
  return dateString;
};

const groupByFuturePast = (events: CalendarEvent[]) => {
  const grouped = groupBy(events, (event) => {
    return new Date(event.date).getTime() < Date.now() ? "past" : "future";
  });
  return {
    past: grouped.past || [],
    future: grouped.future || [],
  };
};

// Component to display a list of calendar events
const CalendarEventList = ({
  events,
  sort = "desc",
}: {
  events: CalendarEvent[];
  sort?: "asc" | "desc";
}) => {
  const groupedEvents = groupByDate(events);
  const sortedDates = Object.keys(groupedEvents).sort((a, b) =>
    sort === "desc"
      ? new Date(b).getTime() - new Date(a).getTime()
      : new Date(a).getTime() - new Date(b).getTime()
  );

  if (!sortedDates.length) {
    return (
      <div className="bg-neutral-100 dark:bg-neutral-800  rounded-xs p-8 flex flex-col justify-center items-center">
        <p className="text-p-base dark:text-white font-medium mb-2">
          No events
        </p>
        <p className="text-p-xs dark:text-neutral-300">
          There are currently no events scheduled right now
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {sortedDates.map((dateString) => {
        const date = new Date(`${dateString}`);

        return (
          <div
            className="grid grid-cols-1 md:grid-cols-[1fr_6fr] gap-4"
            key={dateString}
          >
            <div className="h-full w-full flex-shrink-0 bg-neutral-100 dark:bg-neutral-800 dark:text-white rounded-xs p-4 text-left ">
              <p className="text-p-lg">
                {date.toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "short",
                  timeZone: "UTC", // the grouped date is in UTC not the client timezone
                })}
              </p>
              <p className="text-sm">
                {date.toLocaleDateString("en-US", {
                  weekday: "long",
                  timeZone: "UTC", // the grouped date is in UTC not the client timezone
                })}
              </p>
            </div>
            <div className="flex flex-col gap-4 flex-grow">
              {groupedEvents[dateString]?.map((event, index) => (
                <CalendarEventCard key={index} event={event} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Component to display a single calendar event
const CalendarEventCard = ({ event }: { event: CalendarEvent }) => {
  const renderAction = () => {
    const showAddToCal = new Date(event.date) > new Date();
    const addToCalLink = getCalLink(event);

    if (!isReleaseEvent(event)) {
      return (
        <div className="flex w-full flex-col gap-2 items-center">
          <Button
            href={event?.link}
            as="a"
            className="w-full"
            size="small"
            variant="primary"
            target="_blank"
          >
            <p>Learn More</p>
            <External className="h-4 w-4" />
          </Button>
          {showAddToCal && (
            <Button
              href={addToCalLink}
              as="a"
              className="w-full"
              size="small"
              variant="secondary"
              target="_blank"
            >
              <CalendarDaysIcon className="h-4 w-4" />
              <p>Add to Calendar</p>
            </Button>
          )}
        </div>
      );
    }

    // show add to calendar
    if (showAddToCal) {
      return (
        <div className="flex w-full flex-col gap-2 items-center">
          <Button
            href={addToCalLink}
            as="a"
            className="w-full"
            size="small"
            variant="secondary"
            target="_blank"
          >
            <CalendarDaysIcon className="h-4 w-4" />
            <p>Add to Calendar</p>
          </Button>
          <p className="text-xs text-right text-neutral-400 dark:text-neutral-500">
            Coming Soon
          </p>
        </div>
      );
    }

    // show secondary button
    if (event.collection?.mintState === MintState.Complete) {
      return (
        <div className="flex w-full flex-col gap-2 items-center">
          <Button
            as="Link"
            href={event.link || ""}
            className="w-full"
            size="small"
            variant="secondary"
          >
            View Secondary
          </Button>
          <p className="text-xs text-right text-neutral-400 dark:text-neutral-500">
            Mint Complete
          </p>
        </div>
      );
    }

    // show primary purchase button
    return (
      <div className="w-full">
        <Button
          as="Link"
          href={event.link || ""}
          className="w-full"
          size="small"
        >
          <BrandText>PURCHASE</BrandText>
        </Button>
      </div>
    );
  };

  const renderEventLabel = () => {
    const baseClass = "px-[5px] rounded-full text-[10px]";
    if (isReleaseEvent(event)) {
      return (
        <p
          className={`${baseClass} bg-neutral-100 dark:bg-neutral-800 dark:text-neutral-400 text-neutral-500`}
        >
          Release
        </p>
      );
    }
    return (
      <p
        className={`${baseClass} bg-orange-100 dark:bg-yellow-900/40 dark:text-orange-300 text-orange-900`}
      >
        Event
      </p>
    );
  };

  return (
    <div className="border dark:border-neutral-800 hover:shadow-sm rounded-xs w-full flex flex-col md:flex-row gap-4 justify-between items-center  md:p-4">
      <Link
        href={event.link || ""}
        className="w-full flex flex-col md:flex-row md:items-center gap-4 flex-grow"
      >
        {event.imageUrl ? (
          <img
            className="w-full  h-32 md:h-20 md:w-20  object-cover rounded-xs"
            src={event.imageUrl}
          />
        ) : null}

        <div className="flex flex-col gap-1 px-4">
          <h3 className="font-medium dark:text-white">{event.name}</h3>
          <p className="dark:text-neutral-300 text-p-s text-neutral-600 -mt-[2px]">
            {event.description}
          </p>

          <div className="flex items-center gap-2">
            {event.hideStartTime ? null : (
              <p className="text-p-xs text-neutral-500">
                {new Date(event.date).toLocaleTimeString(undefined, {
                  hour: "numeric",
                  minute: "2-digit",
                  timeZoneName: "short",
                  timeZone: event.timezone || undefined,
                })}
              </p>
            )}

            {renderEventLabel()}
          </div>

          {/* Show Relases specific data */}
          {isReleaseEvent(event) && event.collection?.supplyMax ? (
            <>
              <div className="flex items-center gap-4 md:hidden">
                <p className="text-p-xs text-neutral-500 dark:text-neutral-300">
                  {event.collection?.supplyMax} Works
                </p>

                {/* Hiding price until we have more reliable price data */}
                {/* <div className="flex items-center text-p-xs gap-1 text-neutral-500 dark:text-neutral-300">
                  <p>Price</p>{" "}
                  <EthLabel
                    size="xsmall"
                    amount={
                      event.collection?.stats?.mostRecentMintPrice
                        ? event.collection?.stats?.mostRecentMintPrice
                        : event.collection?.stats?.floorPrice || 0
                    }
                  />
                </div> */}
              </div>
            </>
          ) : null}
        </div>
      </Link>

      {/* Show Release specific data */}
      {isReleaseEvent(event) && event.collection?.supplyMax ? (
        <>
          <div className="w-32 hidden md:block">
            <p className="uppercase text-[10px] text-neutral-600 dark:text-neutral-300 tracking-[0.1em]">
              Supply
            </p>
            <p className="font-medium text-sm dark:text-white">
              {event.collection?.supplyMax}
            </p>
          </div>

          {/* Hiding price until we have more reliable price data */}
          {/* <div className="w-32 hidden md:block">
            <p className="uppercase text-[10px] text-neutral-600 tracking-[0.1em]  dark:text-neutral-300">
              Price
            </p>
            <EthLabel
              size="small"
              amount={
                event.collection?.stats?.mostRecentMintPrice
                  ? event.collection?.stats?.mostRecentMintPrice
                  : event.collection?.stats?.floorPrice || 0
              }
            />
          </div> */}
        </>
      ) : null}

      <div className="w-full md:w-40 flex-shrink-0 md:px-0 md:pb-0 px-4 pb-4 flex justify-end">
        {renderAction()}
      </div>
    </div>
  );
};

const isReleaseEvent = (event: CalendarEvent): event is ReleaseEvent => {
  return "collection" in event;
};

const LoadingState = () => (
  <div className="mx-auto text-center flex flex-col items-center gap-1 py-24 rounded-xs">
    <Spinner className="h-12 w-12 text-neutral-400 dark:text-neutral-500 mb-2 animate-spin" />
    <p className="font-medium text-p-lg dark:text-white">Loading events...</p>
    <p className="text-neutral-600 dark:text-neutral-500">
      Finding calendar events
    </p>
  </div>
);

const ErrorState = ({ error }: { error: string }) => (
  <div className="mx-auto text-center flex flex-col items-center gap-1 py-24 rounded-xs">
    <Close className="h-12 w-12 text-red-500 dark:text-red-400 mb-2" />
    <p className="font-medium text-p-lg dark:text-white">
      Error loading events
    </p>
    <p className="text-neutral-600 dark:text-neutral-500">{error}</p>
  </div>
);

const getCalLink = (event: CalendarEvent) => {
  return isReleaseEvent(event)
    ? formatGoogleCalendarLinkForCollection({
        collectionName: event.collection.name ?? "",
        artistName: event.collection.artistName ?? "",
        startsAt: new Date(event.date),
        slugOrId: event.collection.slug || event.collection.id || "",
      })
    : formatGoogleCalendarLink({
        name: event.name,
        startsAt: new Date(event.date),
      });
};

export default CalendarPage;
